import React, { useRef, useEffect, useState } from "react";
import "./cameraBox.css"; // Assuming you have a CSS file for styles
import style from "../CSS/cameraBox.module.css";
import { PREPAY, IOU, STOLEN_PLATE } from "../Constant/constant";
import { Howl } from "howler";
import soundFile from "./Modals/alert1.wav";
import { resumeAudio } from "../Constant/audio";

// Global beep manager to handle all pump beeps
// Each pump keeps ONE Howl that is replayed every cycle, and the next cycle is
// scheduled up front rather than from onend. That way a play that fails (for
// example because iOS suspended the audio context) can never leave the pump
// permanently silent - the loop simply retries on the next tick.
export const BeepManager = {
  activePumps: new Map(), // Map to store pump beep states

  // 11 sec audio + 30 sec wait, measured from the start of each cycle
  CYCLE_INTERVAL_MS: 41000,

  // Start beep loop for a pump
  startBeepLoop(pumpName, offenceType) {
    // If pump already has an active loop with same offence, don't restart
    if (this.activePumps.has(pumpName)) {
      const existing = this.activePumps.get(pumpName);
      if (existing.offenceType === offenceType) {
        return; // Same offence already playing, don't restart
      } else {
        // Different offence, stop existing and start new
        this.stopBeepLoop(pumpName);
      }
    }

    const pumpState = {
      offenceType,
      sound: null,
      cycleTimer: null,
      retryTimer: null,
      lastPlayedAt: 0,
      startTime: Date.now()
    };

    this.activePumps.set(pumpName, pumpState);
    this.playBeepCycle(pumpName);
  },

  // Build the single Howl reused by every cycle of this pump. Reusing it avoids
  // leaking one Howl per beep, which used to pile up over a multi-day session.
  createSound(pumpName) {
    return new Howl({
      src: [soundFile],
      loop: false,
      autoplay: false,
      volume: 1.0,
      onloaderror: (id, err) => {
        console.error(`[BEEP LOAD ERROR] Pump: ${pumpName}`, err);
      },
      onplayerror: (id, err) => {
        console.error(`[BEEP PLAY ERROR] Pump: ${pumpName}`, err);
        // Most likely a suspended audio context - resume and retry shortly.
        // The regular cycle timer is still pending either way.
        resumeAudio();
        const pumpState = this.activePumps.get(pumpName);
        if (!pumpState) return;
        if (pumpState.retryTimer) clearTimeout(pumpState.retryTimer);
        pumpState.retryTimer = setTimeout(() => {
          if (this.activePumps.has(pumpName)) this.playSound(pumpName);
        }, 1000);
      }
    });
  },

  // Play the pump's sound once, recreating the Howl if it was unloaded
  playSound(pumpName) {
    const pumpState = this.activePumps.get(pumpName);
    if (!pumpState) return;

    resumeAudio();

    if (!pumpState.sound) {
      pumpState.sound = this.createSound(pumpName);
    }

    try {
      pumpState.sound.stop(); // guard against overlapping playback
      pumpState.sound.play();
      pumpState.lastPlayedAt = Date.now();
    } catch (err) {
      console.error(`[BEEP ERROR] Pump: ${pumpName}`, err);
    }
  },

  // Play single beep cycle and schedule the next one
  playBeepCycle(pumpName) {
    const pumpState = this.activePumps.get(pumpName);
    if (!pumpState) return;

    // Schedule the next cycle BEFORE playing, so a failure to play cannot break
    // the chain the way the old onend-driven loop did.
    if (pumpState.cycleTimer) clearTimeout(pumpState.cycleTimer);
    pumpState.cycleTimer = setTimeout(() => {
      if (this.activePumps.has(pumpName)) this.playBeepCycle(pumpName);
    }, this.CYCLE_INTERVAL_MS);

    this.playSound(pumpName);
    // console.log(`[BEEP STARTED] Pump: ${pumpName}, Offence: ${pumpState.offenceType}`);
  },

  // Stop beep loop for a pump
  stopBeepLoop(pumpName) {
    const pumpState = this.activePumps.get(pumpName);
    if (!pumpState) return;

    // Stop and release the sound
    if (pumpState.sound) {
      pumpState.sound.stop();
      pumpState.sound.unload();
      pumpState.sound = null;
    }

    if (pumpState.cycleTimer) {
      clearTimeout(pumpState.cycleTimer);
      pumpState.cycleTimer = null;
    }

    if (pumpState.retryTimer) {
      clearTimeout(pumpState.retryTimer);
      pumpState.retryTimer = null;
    }

    this.activePumps.delete(pumpName);
    // console.log(`[BEEP STOPPED] Pump: ${pumpName}`);
  },

  // Called when the tab becomes visible again or the user interacts. Background
  // tabs on iOS have their timers throttled or frozen, so a pump can be overdue
  // for a beep by the time we come back - fire it immediately instead of waiting.
  wakeUp() {
    const now = Date.now();
    for (const [pumpName, pumpState] of this.activePumps) {
      if (now - pumpState.lastPlayedAt >= this.CYCLE_INTERVAL_MS) {
        this.playBeepCycle(pumpName);
      }
    }
  },

  // Check if pump should beep based on offence type
  shouldBeep(offenceType) {
    return offenceType === PREPAY || offenceType === STOLEN_PLATE;
  },

  // Clean up all beeps (useful for component unmount)
  cleanup() {
    for (const pumpName of [...this.activePumps.keys()]) {
      this.stopBeepLoop(pumpName);
    }
  }
};

const CameraBox = ({ camera, openPopup, setCameraData, pumpCount }) => {
  const boxRef = useRef(null);
  const [isShaking, setIsShaking] = useState(false); // State to control shaking animation
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);

  // Handle beep logic based on camera props
  useEffect(() => {
    if (!camera.pumpName) return;

    if (BeepManager.shouldBeep(camera.previousOffenceType)) {
      // Start beep loop for this pump with this offence
      BeepManager.startBeepLoop(camera.pumpName, camera.previousOffenceType);
    } else {
      // Stop beep loop if offence type doesn't require beeping
      BeepManager.stopBeepLoop(camera.pumpName);
    }

    // Cleanup function for when component unmounts or camera changes
    return () => {
      // Only cleanup if this pump no longer needs beeping
      if (!BeepManager.shouldBeep(camera.previousOffenceType)) {
        BeepManager.stopBeepLoop(camera.pumpName);
      }
    };
  }, [camera.pumpName, camera.previousOffenceType]);

  // Cleanup all beeps when component unmounts
  useEffect(() => {
    return () => {
      // Note: We don't cleanup all beeps here as other CameraBox components
      // might still need their beeps. Each pump manages its own beep lifecycle.
    };
  }, []);


  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;


    const handleMouseDown = (e) => {
      setIsMouseDown(true);
      setStartX(e.clientX);
    };


    const handleMouseMove = (e) => {
      if (!isMouseDown) return;
      const deltaX = e.clientX - startX;
      handleSwipe(deltaX);
    };


    const handleMouseUp = () => {
      setIsMouseDown(false);
    };


    const handleTouchStart = (e) => {
      setStartX(e.touches[0].clientX);
    };


    const handleTouchEnd = (e) => {
      const deltaX = e.changedTouches[0].clientX - startX;
      handleSwipe(deltaX);
    };


    const handleSwipe = (deltaX) => {
      if (Math.abs(deltaX) < 50) return; // Minimum swipe distance
      if (deltaX > 0) {
        //  console.log(`Swiped right on pump ${camera.id}`);
        handleSwipeRight(camera);
      } else {
        //  console.log(`Swiped left on pump ${camera.id}`);
        handleSwipeLeft(camera);
      }
    };


    box.addEventListener("mousedown", handleMouseDown);
    box.addEventListener("mousemove", handleMouseMove);
    box.addEventListener("mouseup", handleMouseUp);
    box.addEventListener("mouseleave", handleMouseUp);
    box.addEventListener("touchstart", handleTouchStart, { passive: true });
    box.addEventListener("touchend", handleTouchEnd, { passive: true });


    return () => {
      box.removeEventListener("mousedown", handleMouseDown);
      box.removeEventListener("mousemove", handleMouseMove);
      box.removeEventListener("mouseup", handleMouseUp);
      box.removeEventListener("mouseleave", handleMouseUp);
      box.removeEventListener("touchstart", handleTouchStart);
      box.removeEventListener("touchend", handleTouchEnd);
    };
  }, [camera.id, isMouseDown, startX]);


  const getDataFromSessionStorage = (pumpName, currentIndex) => {
    try {
      const storedData = JSON.parse(localStorage.getItem(pumpName));


      if (!storedData || storedData.length === 0) {
        //  console.log(`No data found for pump ${pumpName}`);
        return null;
      }


      const data = storedData[currentIndex];


      setCameraData((prevCameraData) => {
        return prevCameraData.map((cameraItem) => {
          if (cameraItem.pumpName === pumpName) {
            let updatedPreviousOffenceType = data.previousOffenceType;
            if (
              storedData.length > 1 &&
              currentIndex === storedData.length - 1 &&
              data.previousOffenceType === "previousEvent"
            ) {
              updatedPreviousOffenceType = undefined;
            }
            return {
              ...cameraItem,
              eventID: data.eventId,
              ocr: data.ocr,
              previousOffenceType: updatedPreviousOffenceType,
            };
          }
          return cameraItem;
        });
      });


      return data;
    } catch (error) {
      console.error("Error retrieving data from session storage:", error);
      return null;
    }
  };


  const handleSwipeLeft = (camera) => {
    const storedData = JSON.parse(localStorage.getItem(camera.pumpName));
    if (!storedData || storedData.length === 0) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 1000);
      return;
    }


    const currentIndex = camera.ocr
      ? storedData.findIndex((data) => data.ocr === camera.ocr)
      : -1;


    if (currentIndex < storedData.length - 1) {
      getDataFromSessionStorage(camera.pumpName, currentIndex + 1);
    } else {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 1000);
    }
  };


  const handleSwipeRight = (camera) => {
    const storedData = JSON.parse(localStorage.getItem(camera.pumpName));
    if (!storedData || storedData.length === 0) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 1000);
      return;
    }


    if (!camera.ocr) {
      // If no current data, show the most recent event
      const data = getDataFromSessionStorage(
        camera.pumpName,
        storedData.length - 1
      );
      if (
        data &&
        data.previousOffenceType !== IOU &&
        data.previousOffenceType !== PREPAY
      ) {
        setCameraData((prevCameraData) => {
          return prevCameraData.map((cameraItem) => {
            if (cameraItem.pumpName === camera.pumpName) {
              return {
                ...cameraItem,
                previousOffenceType: "previousEvent",
              };
            }
            return cameraItem;
          });
        });
      }
    } else {
      const currentIndex = storedData.findIndex(
        (data) => data.ocr === camera.ocr
      );
      if (currentIndex > 0) {
        getDataFromSessionStorage(camera.pumpName, currentIndex - 1);
      } else {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 1000);
      }
    }
  };


  const getOffenceData = (camera) => {
    let backgroundColor, offenceText;


    if (!camera.pumpName) {
      backgroundColor = "#0e0e0ea6";
      offenceText = "";
      return { backgroundColor, offenceText };
    }


    // Default case to be used if `previousOffenceType` doesn't match any case
    const defaultCase = () => {
      backgroundColor = "rgba(0, 128, 0, 0.644)";
      offenceText = "BAY WAITING";
    };


    // Switch statement to determine the backgroundColor and offenceText
    switch (camera.previousOffenceType) {
      case IOU:
        backgroundColor = "rgba(222, 139, 17, 0.8)";
        offenceText = "PAYMENT DUE";
        break;
      case PREPAY:
        backgroundColor = "rgba(208, 24, 24, 0.70)";
        offenceText = "PREPAY REQUIRED";
        // handleBeepLogic();
        break;
      case STOLEN_PLATE:
        backgroundColor = "rgba(225, 95, 95, 0.644)";
        offenceText = "STOLEN PLATE";
        // handleBeepLogic();
        break;
      case undefined:
        backgroundColor = "rgba(0, 128, 0, 0.644)";
        offenceText = "OK TO PUMP";
        break;
      case "previousEvent":
        backgroundColor = "rgb(72, 212, 230, 0.75)";
        offenceText = "OK TO PUMP";
        break;
      default:
        defaultCase();
    }


    // If previousOffenceType is "manualDetect", only change the offenceText
    if (camera.previousOffenceType === "manualDetect") {
      offenceText = "CHECKING";
    }


    return { backgroundColor, offenceText };
  };


  const getClassNames = (numBoxes) => {
    if (numBoxes <= 9) {
      return {
        regoClass: style.regoScale9,
        gridStatusClass: style.gridStatusScale9,
      };
    } else if (numBoxes <= 16) {
      return {
        regoClass: style.regoScale16,
        gridStatusClass: style.gridStatusScale16,
      };
    } else if (numBoxes <= 25) {
      return {
        regoClass: style.regoScale25,
        gridStatusClass: style.gridStatusScale25,
      };
    } else if (numBoxes <= 36) {
      return {
        regoClass: style.regoScale36,
        gridStatusClass: style.gridStatusScale36,
      };
    } else {
      return {
        regoClass: style.regoScale42,
        gridStatusClass: style.gridStatusScale42,
      };
    }
  };
  const handleClick = () => {
    // console.log(`Clicked/tapped on pump ${camera.id}`);
    openPopup(camera);
  };


  // Call getOffenceData with the camera object to get the background color and offence text
  const { backgroundColor, offenceText } = getOffenceData(camera);
  // Retrieve pumpCount from session storage
  const { regoClass, gridStatusClass } = getClassNames(Number(pumpCount));
  const boxClass = camera.pumpName ? "grid-used" : "grid-unused";


  return (
    <div
      ref={boxRef}
      className={`grid-item ${boxClass} ${isShaking ? "shaking-div" : ""}`}
      onClick={handleClick}
      style={{ background: backgroundColor }}
    >
      {camera.pumpName ? (
        <>
          {/* <div className={style.cameraId}>{camera.id}</div> */}
          <div className={`grid-label`}>{camera.id}</div>
          <div className="grid-center">
            {camera.ocr && ( // Only render the OCR value if it exists and is not empty
              <div className="plate">
                <div className="platebg">
                  <span className={`rego ${regoClass}`}>{camera.ocr}</span>
                </div>
                <div className="ball"></div>
              </div>
            )}
            <br />
          </div>
          <div className={`grid-status ${gridStatusClass}`}>{offenceText}</div>
        </>
      ) : (
        <>
          <div className={style.cameraBoxMiddleData}>
            <br />
            <div className={style.NotUsedBoxText}>{offenceText}</div>
          </div>
        </>
      )}
    </div>
  );
};


export default CameraBox;